import React from 'react';
import { motion } from 'framer-motion';
import { Heart, MapPin, Gift, Calendar, ArrowDown } from 'lucide-react';

/**
 * Modern Template - Estilo minimalista e contemporâneo
 * Paleta: clara com contraste
 * Tipografia: sem serifa
 * Animações: mais objetivas (slide / stagger)
 */

const slideUp = {
  initial: { opacity: 0, y: 60 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" },
  transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }
};

const slideLeft = {
  initial: { opacity: 0, x: 60 },
  whileInView: { opacity: 1, x: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }
};

const staggerContainer = {
  initial: {},
  whileInView: {
    transition: { staggerChildren: 0.08 }
  },
  viewport: { once: true }
};

const staggerItem = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.5 }
};

export function ModernTemplate({ siteData, presentes, onPresentear, formatCurrency }) {
  const {
    couple_names = '',
    wedding_date = '',
    wedding_location = '',
    story = '',
    message = '',
    cover_image_url = '',
    gallery_images = []
  } = siteData || {};

  const formattedDate = wedding_date ? new Date(wedding_date).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }) : '';

  const longDate = wedding_date ? new Date(wedding_date).toLocaleDateString('pt-BR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  }) : '';

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', 'Helvetica Neue', sans-serif" }}>
      {/* Hero Section - Full Screen Split */}
      <section className="min-h-screen relative overflow-hidden">
        <div className="grid lg:grid-cols-2 min-h-screen">
          {/* Left Side - Text */}
          <div className="flex flex-col justify-center px-8 md:px-16 lg:px-20 py-20 bg-zinc-50 order-2 lg:order-1">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <span className="text-xs tracking-[0.3em] uppercase text-zinc-400 mb-6 block">
                Vamos casar
              </span>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-zinc-900 leading-tight mb-8">
                {couple_names || 'Nosso Casamento'}
              </h1>
              
              <div className="space-y-3 text-zinc-600">
                {formattedDate && (
                  <motion.div 
                    className="flex items-center gap-3"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 }}
                  >
                    <Calendar size={18} className="text-emerald-500" />
                    <span className="text-lg">{longDate}</span>
                  </motion.div>
                )}
                
                {wedding_location && (
                  <motion.div 
                    className="flex items-center gap-3"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.6 }}
                  >
                    <MapPin size={18} className="text-emerald-500" />
                    <span className="text-lg">{wedding_location}</span>
                  </motion.div>
                )}
              </div>

              <motion.div 
                className="mt-12"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8 }}
              >
                <div className="flex items-center gap-2 text-zinc-400">
                  <ArrowDown size={16} className="animate-bounce" />
                  <span className="text-xs tracking-wider uppercase">Role para mais</span>
                </div>
              </motion.div>
            </motion.div>
          </div>
          
          {/* Right Side - Image */}
          <motion.div 
            className="relative min-h-[50vh] lg:min-h-screen order-1 lg:order-2"
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1 }}
          >
            {cover_image_url ? (
              <img 
                src={cover_image_url} 
                alt="Capa do casamento"
                className="absolute inset-0 w-full h-full object-cover object-center"
                style={{ objectPosition: 'center 30%' }}
              />
            ) : (
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-400 via-teal-500 to-cyan-600" />
            )}
            
            {/* Overlay Pattern */}
            <div className="absolute inset-0 bg-black/10" />
          </motion.div>
        </div>
      </section>

      {/* Message Section */}
      {message && (
        <motion.section 
          className="py-24 md:py-32 bg-white"
          {...slideUp}
        >
          <div className="max-w-4xl mx-auto px-8">
            <motion.div {...slideUp}>
              <div className="relative">
                <span className="absolute -top-8 -left-4 text-8xl text-emerald-100 font-serif">"</span>
                <p className="text-2xl md:text-3xl lg:text-4xl text-zinc-800 font-light leading-relaxed relative z-10">
                  {message}
                </p>
                <span className="absolute -bottom-16 -right-4 text-8xl text-emerald-100 font-serif rotate-180">"</span>
              </div>
            </motion.div>
          </div>
        </motion.section>
      )}

      {/* Story Section */}
      {story && (
        <motion.section 
          className="py-24 md:py-32 bg-zinc-50"
          {...slideUp}
        >
          <div className="max-w-4xl mx-auto px-8">
            <motion.div {...slideUp} className="mb-12">
              <span className="text-xs tracking-[0.3em] uppercase text-emerald-600 mb-4 block">
                Como tudo começou
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-zinc-900">
                Nossa História
              </h2>
            </motion.div>
            <motion.p 
              {...slideLeft}
              className="text-lg md:text-xl text-zinc-600 leading-relaxed whitespace-pre-line"
            >
              {story}
            </motion.p>
          </div>
        </motion.section>
      )}

      {/* Gallery Section */}
      {gallery_images.length > 0 && (
        <motion.section 
          className="py-24 md:py-32 bg-white"
          {...slideUp}
        >
          <div className="max-w-7xl mx-auto px-8">
            <motion.div {...slideUp} className="mb-12">
              <span className="text-xs tracking-[0.3em] uppercase text-emerald-600 mb-4 block">
                Momentos especiais
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-zinc-900">
                Galeria
              </h2>
            </motion.div>
            
            <motion.div 
              className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4"
              {...staggerContainer}
            >
              {gallery_images.map((img, index) => (
                <motion.div
                  key={index}
                  {...staggerItem}
                  className={`overflow-hidden ${
                    index === 0 ? 'col-span-2 row-span-2' : ''
                  }`}
                >
                  <motion.img 
                    src={img} 
                    alt={`Foto ${index + 1}`}
                    className="w-full h-full object-cover aspect-square"
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.3 }}
                  />
                </motion.div>
              ))}
            </motion.div>
          </div>
        </motion.section>
      )}

      {/* Gift List Section */}
      {presentes && presentes.length > 0 && (
        <motion.section 
          className="py-24 md:py-32 bg-zinc-900 text-white"
          {...slideUp}
        >
          <div className="max-w-6xl mx-auto px-8">
            <motion.div {...slideUp} className="text-center mb-16">
              <span className="text-xs tracking-[0.3em] uppercase text-emerald-400 mb-4 block">
                Sua presença é o melhor presente
              </span>
              <h2 className="text-3xl md:text-4xl font-bold">
                Lista de Presentes
              </h2>
            </motion.div>
            
            <motion.div 
              className="grid gap-4 md:grid-cols-2 lg:grid-cols-3"
              {...staggerContainer}
            >
              {presentes.map((presente, index) => (
                <motion.div
                  key={presente.id}
                  {...staggerItem}
                  className="group bg-zinc-800 p-8 hover:bg-zinc-700 transition-colors duration-300"
                >
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-xl font-semibold text-white">{presente.titulo}</h3>
                    <Gift className="text-emerald-400" size={24} />
                  </div>
                  {presente.descricao && (
                    <p className="text-zinc-400 text-sm mb-6">{presente.descricao}</p>
                  )}
                  <div className="flex items-end justify-between">
                    <span className="text-3xl font-bold text-emerald-400">
                      {formatCurrency(presente.valor)}
                    </span>
                    <button
                      onClick={() => onPresentear(presente)}
                      className="px-6 py-3 bg-emerald-500 text-zinc-900 font-semibold text-sm hover:bg-emerald-400 transition-colors duration-300"
                      data-testid={`gift-btn-${presente.id}`}
                    >
                      Presentear →
                    </button>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </motion.section>
      )}

      {/* Footer */}
      <footer className="py-8 bg-zinc-950 text-center">
        <p className="text-zinc-500 text-sm flex items-center justify-center gap-2">
          Feito com <Heart className="text-emerald-500" size={14} /> usando 
          <span className="font-semibold text-zinc-400">liventy</span>
        </p>
      </footer>
    </div>
  );
}

export default ModernTemplate;
